import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Bell } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { TFunction } from "@/i18n/catalog";
import { useTranslation } from "@/i18n/use-translation";
import { formatRelative } from "@/lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type NotificationItem,
} from "@/hooks/use-notifications";

export function NotificationBadge() {
  const { locale, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const query = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const items = query.data?.items ?? [];
  const unread = query.data?.unreadCount ?? 0;

  function handleOpenNotification(item: NotificationItem) {
    if (!item.read_at) markRead.mutate(item.id);
    const { waste_event_id, comment_id } = item.payload;
    if (waste_event_id) {
      // Preserve any filters already on the URL when we're on Transactions; from any
      // other page the Transactions page restores them from the persisted store on
      // mount (POS2-852). Only commentEvent/comment come from the notification.
      const params = new URLSearchParams(
        location.pathname === "/waste/transactions" ? location.search : "",
      );
      params.set("commentEvent", waste_event_id);
      if (comment_id) params.set("comment", comment_id);
      else params.delete("comment");
      navigate(`/waste/transactions?${params.toString()}`);
    }
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative text-muted-foreground"
          aria-label={
            unread > 0 ? t("nav.notificationsUnread", { count: unread }) : t("nav.notifications")
          }
          data-testid="notification-badge"
        >
          <Bell className="size-4" aria-hidden />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-4 text-white"
              data-testid="notification-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-dropdown">
        <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
          <span className="text-sm font-medium text-gray-900">{t("nav.notifications")}</span>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-xs text-gray-500"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              data-testid="notification-mark-all"
            >
              {t("nav.markAllRead")}
            </Button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-gray-500">
              {t("nav.noNotifications")}
            </p>
          ) : (
            <ul>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleOpenNotification(item)}
                    className="flex w-full gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
                    data-testid="notification-item"
                  >
                    <span
                      className={`mt-1.5 size-2 shrink-0 rounded-full ${
                        item.read_at ? "bg-transparent" : "bg-blue-500"
                      }`}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-gray-900">
                        {notificationText(t, item)}
                      </span>
                      {item.payload.snippet && (
                        <span className="mt-0.5 block truncate text-xs text-gray-500">
                          {item.payload.snippet}
                        </span>
                      )}
                      <span className="mt-0.5 block text-xs text-gray-400">
                        {formatRelative(locale, item.created_at)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function notificationText(t: TFunction, item: NotificationItem): string {
  const actor = item.payload.actor_name ?? t("nav.someone");
  if (item.type === "comment_mention") return t("nav.mentionedYou", { actor });
  return t("nav.sentNotification", { actor });
}
